import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, scheduleNextField, type Recurrence, type SaveRecurrenceDefinition, type ScheduleNext } from './types';

// PUT /recurrence_definitions/:zone_name/:name — update. Backend:
// `RecurrenceDefinitionService.update` returns the `UPDATE … RETURNING` row with
// `zone_name` echoed from the resolved new zone + the shared `recurrences` /
// `folder_path` serializers.
//
// `UpdatedRecurrenceDefinition` is its OWN type + schema for the same reason as
// `CreatedRecurrenceDefinition` — a distinct backend statement that happens to
// return the same shape. Structural typing lets it flow wherever a
// `RecurrenceDefinition` is expected downstream.
export interface UpdatedRecurrenceDefinition {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error: boolean;
  readonly schedule_next: ScheduleNext;
  readonly recurrences: readonly Recurrence[];
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

const updatedSchema = object<UpdatedRecurrenceDefinition>({
  recurrence_definition_id: num(REJECT),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  schedule_next: scheduleNextField,
  recurrences: array(recurrenceSchema),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

/**
 * PUT `/recurrence_definitions/:zone_name/:name`. The URL params identify the
 * *original* record (pre-edit); the body carries the new values, which may
 * include changes to `name` and `zone_name`. Returns the updated record.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const putRecurrenceDefinition = async (
  original: { zone_name: string; name: string },
  save: SaveRecurrenceDefinition
): Promise<Received<UpdatedRecurrenceDefinition>> => {
  const res = await api.put(`/recurrence_definitions/${encodeURIComponent(original.zone_name)}/${encodeURIComponent(original.name)}`, save);
  return readResponse(res.data, { schema: updatedSchema, source: 'putRecurrenceDefinition' });
};
